import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { pushGuide } from '@/lib/api/guideSync';
import { useTvControllers } from '@/lib/api/tvController';
import { useTvControllerGroups } from '@/lib/api/tvControllerGroup';
import { groupTvs, guidesByTag, guideStatus } from '@/lib/schema/guideSync';
import { TvController } from '@/lib/schema/tvController';

type GuideRef = { id?: string; name?: string; hash?: string };

/** Shown on the channel guide pages when TVs of the groups using this guide
 *  do not hold its current version — typically right after a save. Pushing is
 *  explicit so a guide edit never reloads a venue's TVs without a click. */
export function GuideSyncBanner({ guide }: { guide?: GuideRef }) {
  const [values] = useTvControllers();
  const [groups] = useTvControllerGroups();
  const [pushed, setPushed] = useState<string | null>(null);

  const { groupNames, behind, pending } = useMemo(() => {
    const users = groups.filter(
      (g) => g.channelGuideId && g.channelGuideId === guide?.id
    );
    const tvs = users.flatMap((g) =>
      groupTvs(g, (values || []) as TvController[])
    );
    const byTag = guidesByTag(groups);
    const guides = new Map([[guide?.id ?? '', guide ?? {}]]);
    const seen = new Set<string>();
    const behindTvs = tvs.filter((tv) => {
      if (seen.has(tv.ip)) return false;
      seen.add(tv.ip);
      const status = guideStatus(tv, byTag, guides);
      return (
        status.guideId === guide?.id &&
        (status.state === 'outdated' || status.state === 'unknown')
      );
    });
    return {
      groupNames: users.map((g) => g.name),
      behind: behindTvs,
      pending: users.length > 0 && !guide?.hash,
    };
  }, [groups, values, guide]);

  if (!guide?.id || groupNames.length === 0) return null;

  if (pending) {
    return (
      <p className="mb-4 rounded-md border p-3 text-sm text-muted-foreground">
        {guide.name ?? 'This guide'}: waiting for the agent to process the
        latest change.
      </p>
    );
  }

  // Hide until the push results come back in the TVs' loadedGuide.
  if (behind.length === 0 || pushed === guide.hash) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
      <span className="flex-1">
        <strong>{guide.name ?? 'This guide'}</strong>: {behind.length} TV(s) in{' '}
        {groupNames.join(', ')} do not have the latest version.
      </span>
      <Button
        size="sm"
        onClick={() => {
          setPushed(guide.hash ?? null);
          pushGuide(
            guide.id!,
            behind.map((tv) => tv.ip),
            guide.name ?? 'Channel guide'
          );
        }}
      >
        Push to {behind.length} TV(s)
      </Button>
    </div>
  );
}
